import React, { useEffect, useState } from 'react';

import { GrClear } from 'react-icons/gr';
import Masonry from 'react-masonry-css';

import { SearchRequestObject } from '@/api/elasticsearch/models';
import { searchElasticApi } from '@/api/fetch/rest-search-api';
import { IListingItem } from '@/modules/core/types';
import AdCard from '@/tayara-kit/ListingItemCard/AdCard';
import { AdType } from '@/tayara-kit/ListingItemCard/types';

interface ShopFeedProps {
  shopId: string;
}

const ShopFeed: React.FC<ShopFeedProps> = ({ shopId }) => {
  const [listingItems, setListingItems] = useState<IListingItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchRequest] = useState<SearchRequestObject>({
    query: '',
    offset: 0,
    limit: 30,
    sort: 0,
    filter: {
      categoryId: '',
      subCategoryId: '',
      adParamsMap: {},
      rangeAdParamsMap: {},
      governorate: '',
      delegation: [],
      minPrice: 0,
      maxPrice: 0,
      productTypeList: [],
      state: 2,
      userid: shopId,
    },
    afterTimestamp: 0,
  });

  const handlePageClick = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const fetchData = async () => {
    if (!shopId) return;

    // Update the search request with the current page's offset
    const updatedSearchRequest = {
      ...searchRequest,
      offset: (currentPage - 1) * searchRequest.limit,
      filter: {
        ...searchRequest.filter,
        userid: shopId,
      },
    };

    // Fetch total hits count and listing items
    const [[newHits, totalHitsCount]] = await searchElasticApi(
      updatedSearchRequest,
      false
    );
    setTotalPages(Math.ceil(totalHitsCount / searchRequest.limit));
    setListingItems(newHits);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    fetchData();
  }, [shopId, currentPage]);

  return (
    <div className="w-full flex flex-col items-center justify-center gap-y-6">
      {listingItems.length === 0 ? (
        <div className="flex flex-col items-center w-full px-4 md:w-1/2 mx-auto gap-y-12 my-12">
          <div className="alert alert-warning shadow-lg w-full">
            <div className="flex items-center gap-x-2">
              <GrClear />
              <h1 className="font-arabic">
                Aucune annonce trouvée pour ce shop
              </h1>
            </div>
          </div>
          <img
            src="/media/undraw_not_found_-60-pq.svg"
            alt="No results found for your search on tayara"
            className="w-full max-w-xs"
          />
        </div>
      ) : (
        <>
          <Masonry
            breakpointCols={{
              default: 7,
              1536: 6,
              1280: 5,
              1024: 4,
              768: 3,
              640: 2,
              320: 1,
            }}
            className="flex w-auto justify-around"
            columnClassName="bg-clip-padding pl-[8px] md:pl-[20px] vertical-masonry-margin"
          >
            {listingItems.map((item: IListingItem) => (
              <AdCard adType={AdType.Normal} key={item.id} adCardData={item} />
            ))}
          </Masonry>
          {totalPages > 1 && (
            <div className="flex justify-center mt-4">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`mx-1 py-1 px-3 rounded-md ${
                    currentPage === i + 1
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => handlePageClick(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopFeed;
